import { useEffect } from 'react';

import { Stack, Tooltip } from '@mui/material';

import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import Button from 'domains/core/components/Button';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import useQueryParams from 'hooks/useQueryParams';
import { CampaignStatus } from 'models/enums';

type Props = {
    activeStep: CustomCampaignSteps;
    continueTooltip?: string;
    isContinueEnabled: boolean;
    onContinueClick: () => void;
    onPublishClick: () => void;
    onPublishAndResumeClick: () => void;
    onSaveDraftClick: () => void;
    setContinueTooltip: (tooltip: string) => void;
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
};

// Reusable Save as Draft button component
const SaveAsDraftButton = ({ onClick }: { onClick: () => void }) => (
    <Button onClick={onClick} variant="outlined" sx={{ width: '146px' }} data-testid="save-as-draft-button">
        Save as Draft
    </Button>
);

/**
 *
 * @param onClick - The function to call when the button is clicked.
 * @returns A button that when clicked, will call the onClick function which will be resume functionality.
 */
const ResumeButton = ({ onClick }: { onClick: () => void }) => (
    <Tooltip title="Publish changes and resume curation" arrow>
        <Button onClick={onClick} variant="contained" data-testid="resume-button">
            Resume
        </Button>
    </Tooltip>
);

const CustomCampaignNavigationButtons = ({
    activeStep,
    continueTooltip = '',
    isContinueEnabled,
    onContinueClick,
    onPublishClick,
    onPublishAndResumeClick,
    onSaveDraftClick,
    setContinueTooltip,
    setIsContinueEnabled,
}: Props) => {
    const { canPublishCurations, canManageCurationsDrafts } = useAccessCentrePermissions();
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;

    useEffect(() => {
        // Disabling the continue button with every step change
        // as every step has its own unique validation requirements
        setIsContinueEnabled(false);
        // Reset tooltip so disabled state on other steps shows no tooltip
        setContinueTooltip('');
    }, [activeStep, setContinueTooltip, setIsContinueEnabled]);

    const hideNavigationButtons =
        activeStep === CustomCampaignSteps.CHOOSE_NEXT_STEP ||
        activeStep === CustomCampaignSteps.CHOOSE_NEXT_STEP_CONDITIONAL_SPLIT ||
        activeStep === CustomCampaignSteps.SELECT_PATH_ACTION;
    if (hideNavigationButtons) return;

    if (activeStep === CustomCampaignSteps.PUBLISH_CAMPAIGN) {
        // For paused curations: Save on left, Resume on right
        if (status === CampaignStatus.PAUSED) {
            return (
                <Stack direction="row" spacing={2}>
                    {canPublishCurations && (
                        <Button onClick={onPublishClick} variant="outlined">
                            Save
                        </Button>
                    )}
                    {canPublishCurations && <ResumeButton onClick={onPublishAndResumeClick} />}
                </Stack>
            );
        }

        // For other curations: Save as Draft (optional), Publish
        return (
            <Stack direction="row" spacing={2}>
                {canManageCurationsDrafts && <SaveAsDraftButton onClick={onSaveDraftClick} />}
                {canPublishCurations && (
                    <Button onClick={onPublishClick} variant="outlined" data-testid="publish-button">
                        Publish
                    </Button>
                )}
            </Stack>
        );
    }

    return (
        <Stack direction="row" spacing={2}>
            {canManageCurationsDrafts &&
                status !== CampaignStatus.PAUSED &&
                activeStep !== CustomCampaignSteps.NAME_CAMPAIGN && <SaveAsDraftButton onClick={onSaveDraftClick} />}
            <Tooltip title={!isContinueEnabled && continueTooltip ? continueTooltip : ''} arrow placement="bottom">
                <span>
                    <Button
                        data-testid="continue-button"
                        disabled={!isContinueEnabled}
                        onClick={onContinueClick}
                        variant="outlined"
                    >
                        Continue
                    </Button>
                </span>
            </Tooltip>
        </Stack>
    );
};

export default CustomCampaignNavigationButtons;
